'use client';
import React, { useState } from "react";
import { MdEmail } from "react-icons/md";
import { FaFacebook } from "react-icons/fa";
import { FaTelegram } from "react-icons/fa";
import { AiFillInstagram } from "react-icons/ai";
import { FaTiktok } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { FaWhatsapp } from "react-icons/fa";
import { toast } from "react-toastify";

interface ShareProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  url: string; // URL passed as a prop
}

export const Share: React.FC<ShareProps> = ({ isOpen, setIsOpen, url }) => {
  const lang =
    typeof window !== "undefined" ? window.location.pathname.split("/")[1] : "";
  const isArabic = lang === "ar";

  const translations = {
    header: isArabic ? "مشاركة" : "Share",
    createPost: isArabic ? "شارك الوظيفه عبر" : "Share the job by",
    copy: isArabic ? "نسخ" : "Copy",
    icons: [
      {
        label: isArabic ? "البريد الإلكتروني" : "Email",
        icon: <MdEmail />,
        bg: "bg-gray-600 hover:bg-gray-700",
        link: `mailto:?subject=Check%20this%20out&body=${encodeURIComponent(url)}`,
      },
      {
        label: isArabic ? "WhatsApp" : "WhatsApp",
        icon: <FaWhatsapp />,
        bg: "bg-green-500 hover:bg-green-500",
        link: `https://wa.me/?text=${encodeURIComponent(url)}`,
      },
      {
        label: isArabic ? "Telegram" : "Telegram",
        icon: <FaTelegram />,
        bg: "bg-blue-500 hover:bg-blue-600",
        link: `https://t.me/share/url?url=${encodeURIComponent(url)}`,
      },
      {
        label: isArabic ? "Facebook" : "Facebook",
        icon: <FaFacebook />,
        bg: "bg-blue-700 hover:bg-blue-800",
        link: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      },
      {
        label: isArabic ? "Instagram" : "Instagram",
        icon: <AiFillInstagram />,
        bg: "bg-pink-500 hover:bg-pink-600",
        link: "https://www.instagram.com/", // Instagram doesn't allow direct share links
      },
      {
        label: isArabic ? "TikTok" : "TikTok",
        icon: <FaTiktok />,
        bg: "bg-black hover:bg-gray-900",
        link: "https://www.tiktok.com/", // TikTok doesn't allow direct share links
      },
      {
        label: isArabic ? "X" : "X",
        icon: <FaXTwitter />,
        bg: "bg-gray-600 hover:bg-gray-700",
        link: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`,
      },
    ],
    link: url,
  };

  const closeModal = () => setIsOpen(false);

  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const copyToClipboard = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(translations.link).then(() => {
        toast.success(isArabic ? "تم نسخ الرابط!" : "Link copied!");
      });
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = translations.link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success(isArabic ? "تم نسخ الرابط!" : "Link copied!");
    }
  };

  return (
    <div>
      {/* Modal */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 transition-opacity duration-300 opacity-100"
          onClick={handleOutsideClick}
        >
          <div className="max-w-sm w-full bg-white text-black rounded-lg shadow-lg p-4 transform transition-transform duration-300 scale-100 dark:bg-gray-800 dark:text-white">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">{translations.header}</h2>
              <button
                onClick={closeModal}
                className="bg-gray-200 hover:bg-gray-300 rounded-full w-[34px] h-[34px] p-[5px] flex justify-center items-center dark:bg-gray-700 dark:hover:bg-gray-600"
              >
                <span className="sr-only">Close</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="24px"
                  viewBox="0 -960 960 960"
                  width="24px"
                  fill="currentColor"
                >
                  <path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" />
                </svg>
              </button>
            </div>

            {/* Share Button */}
            <p className="w-full bg-gray-200 text-black text-center rounded-md py-2 mb-4 dark:bg-gray-700 dark:text-white">
              {translations.createPost}
            </p>

            {/* Icons */}
            <div className="grid grid-cols-3 gap-4">
              {translations.icons.map((icon, idx) => (
                <div key={idx} className="flex flex-col items-center">
                  <a
                    href={icon.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${icon.bg} text-white rounded-full w-10 h-10 flex items-center justify-center`}
                  >
                    {icon.icon}
                  </a>
                  <span className="text-sm mt-2">{icon.label}</span>
                </div>
              ))}
            </div>

            {/* Link */}
            <div className="mt-4">
              <div className="flex items-center bg-gray-200 rounded-md p-2 dark:bg-gray-700">
                <input
                  type="text"
                  value={translations.link}
                  className="flex-1 bg-transparent text-sm text-black outline-none dark:text-white"
                  readOnly
                />
                <button
                  onClick={copyToClipboard}
                  className="bg-blue-500 text-white text-sm px-4 py-1 rounded-md hover:bg-blue-400 dark:bg-blue-700 dark:hover:bg-blue-600"
                >
                  {translations.copy}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
